import { useState, useEffect, useRef, useCallback } from 'react';
import { appConfig } from '../config/appConfig';

/**
 * Custom hook for managing GenericFormsPage state
 * Extracted from GenericFormsPage for better organization
 */
export const useGenericFormState = (config) => {
  const { entityName, data: configData = [] } = config;

  // Data and search
  const [data, setData] = useState(configData);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Use ref to track previous configData to prevent infinite loops
  const prevConfigDataRef = useRef(configData);

  // Update data state when config.data changes (only if actually different)
  useEffect(() => {
    // Only update if configData is actually different (not just a new array reference)
    if (configData !== prevConfigDataRef.current && 
        JSON.stringify(configData) !== JSON.stringify(prevConfigDataRef.current)) {
      setData(configData);
      prevConfigDataRef.current = configData;
    }
  }, [configData]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  // Initialize pageSize from localStorage or config, default to appConfig.table.defaultPageSize
  const [pageSize, setPageSize] = useState(() => {
    // Try to get saved preference first
    const savedPageSize = localStorage.getItem(`table-page-size-${entityName || 'default'}`);
    if (savedPageSize) {
      const parsedSize = parseInt(savedPageSize, 10);
      if (!isNaN(parsedSize) && parsedSize > 0) {
        return parsedSize;
      }
    }
    // Fall back to config default or appConfig default
    return config.ui?.defaultPageSize || appConfig.table.defaultPageSize;
  });

  // Table customization
  const [tableDensity, setTableDensity] = useState(config.ui?.defaultTableDensity || 'normal');
  const [tableVariant, setTableVariant] = useState(config.ui?.tableVariant || 'default');
  const [showTablePresets, setShowTablePresets] = useState(false);
  const [tableConfig, setTableConfig] = useState({
    columnWidths: {},
    columnOrder: [],
    sortConfig: []
  });

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(() => 
    config.tableColumns?.reduce((acc, col) => ({ ...acc, [col.key]: true }), {}) || {}
  );

  // Use ref to track previous tableColumns to prevent infinite loops
  const prevTableColumnsRef = useRef(config.tableColumns);

  // Update visible columns when tableColumns change (only if actually different)
  useEffect(() => {
    if (config.tableColumns && config.tableColumns !== prevTableColumnsRef.current) {
      // Compare by stringifying to check if content actually changed
      const currentStr = JSON.stringify(config.tableColumns);
      const prevStr = JSON.stringify(prevTableColumnsRef.current);
      
      if (currentStr !== prevStr) {
        setVisibleColumns(config.tableColumns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {}));
        prevTableColumnsRef.current = config.tableColumns;
      }
    }
  }, [config.tableColumns]);

  // Keep table variant in sync with config changes
  useEffect(() => {
    setTableVariant(config.ui?.tableVariant || 'default');
  }, [config.ui?.tableVariant]);

  // Dynamic board lookup (for board variant)
  const [boardLookupState, setBoardLookupState] = useState(config.ui?.boardLookup || {});
  useEffect(() => {
    setBoardLookupState(config.ui?.boardLookup || {});
  }, [config.ui?.boardLookup]);

  // Modal/Form states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isSplitViewOpen, setIsSplitViewOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formMode, setFormMode] = useState('add');
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(true);
  const [exportLoading, setExportLoading] = useState('');
  const [formNotifications, setFormNotifications] = useState([]);

  // File attachment states
  const [fileListModalOpen, setFileListModalOpen] = useState(false);
  const [selectedRecordFiles, setSelectedRecordFiles] = useState([]);
  const [selectedRecordTitle, setSelectedRecordTitle] = useState('');
  const [filesToDelete, setFilesToDelete] = useState([]);

  // Monitor form errors and update form validity
  useEffect(() => {
    const hasErrors = Object.values(formErrors).some(error => error && error.trim() !== '');
    setIsFormValid(!hasErrors);
  }, [formErrors]);

  // Load saved table density
  useEffect(() => {
    const savedDensity = localStorage.getItem(`table-density-${entityName}`);
    if (savedDensity) {
      setTableDensity(savedDensity);
    }
  }, [entityName]);

  // Load saved page size preference
  useEffect(() => {
    const savedPageSize = localStorage.getItem(`table-page-size-${entityName}`);
    if (savedPageSize) {
      const parsedSize = parseInt(savedPageSize, 10);
      if (!isNaN(parsedSize) && parsedSize > 0) {
        setPageSize(parsedSize);
        return; // Don't override with config if we have a saved preference
      }
    }
    // If no saved preference, use config default or appConfig default
    const defaultSize = config.ui?.defaultPageSize || appConfig.table.defaultPageSize;
    setPageSize(defaultSize);
  }, [entityName, config.ui?.defaultPageSize]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    // Save page size preference to localStorage
    localStorage.setItem(`table-page-size-${entityName}`, newPageSize.toString());
  };

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = (totalPages) => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Reset to first page when filters change (stable identity so page nav is not undone)
  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Form notification helper
  const addFormNotification = (message, type = 'info') => {
    const id = Date.now();
    const notification = { id, message, type };
    setFormNotifications(prev => [...prev, notification]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setFormNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const clearFormNotifications = () => {
    setFormNotifications([]);
  };

  // Table customization handlers
  const handleDensityChange = (newDensity) => {
    setTableDensity(newDensity);
    localStorage.setItem(`table-density-${entityName}`, newDensity);
  };

  const handleTableConfigChange = (newConfig) => {
    setTableConfig(newConfig);
  };

  const handleApplyTableConfig = (config) => {
    if (config.columnWidths) {
      setTableConfig(prev => ({ ...prev, columnWidths: config.columnWidths }));
    }
    if (config.columnOrder) {
      setTableConfig(prev => ({ ...prev, columnOrder: config.columnOrder }));
    }
    if (config.sortConfig) {
      setTableConfig(prev => ({ ...prev, sortConfig: config.sortConfig }));
    }
  };

  const handleApplyVisibleColumns = (newVisibleColumns) => {
    setVisibleColumns(newVisibleColumns);
  };

  return {
    // Data state
    data,
    setData,
    searchTerm,
    setSearchTerm,

    // Pagination state
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    handlePageChange,
    handlePageSizeChange,
    handlePreviousPage,
    handleNextPage,
    resetToFirstPage,

    // Table state
    tableDensity,
    setTableDensity,
    tableVariant,
    setTableVariant,
    showTablePresets,
    setShowTablePresets,
    tableConfig,
    setTableConfig,
    visibleColumns,
    setVisibleColumns,
    boardLookupState,
    setBoardLookupState,

    // Modal/Form state
    isFormOpen,
    setIsFormOpen,
    isViewModalOpen,
    setIsViewModalOpen,
    isSplitViewOpen,
    setIsSplitViewOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    selectedRecord,
    setSelectedRecord,
    formMode,
    setFormMode,
    formData,
    setFormData,
    isLoading,
    setIsLoading,
    formErrors,
    setFormErrors,
    isFormValid,
    setIsFormValid,
    exportLoading,
    setExportLoading,
    formNotifications,
    setFormNotifications,

    // File attachment state
    fileListModalOpen,
    setFileListModalOpen,
    selectedRecordFiles,
    setSelectedRecordFiles,
    selectedRecordTitle,
    setSelectedRecordTitle,
    filesToDelete,
    setFilesToDelete,

    // Handlers
    handleDensityChange,
    handleTableConfigChange,
    handleApplyTableConfig,
    handleApplyVisibleColumns,
    addFormNotification,
    clearFormNotifications
  };
};
